/**
 * Glue between UpCharge (Product Fees) and the theme's native service-fee presentation.
 *
 * The theme renders the fee itself (snippets/cart-summary.liquid) and hides the app's own
 * injected widget, so two things need reconciling:
 *
 *   1. Orphaned fee lines. The fee is a real Shopify cart line and only UpCharge removes it.
 *      When the last product is removed the app's cleanup can lag or not fire, leaving a cart
 *      whose only line is the fee.
 *   2. The fee explanation text. The app holds it in its own settings and writes it onto its
 *      notice icon; hiding that widget also hid the text, so the theme fell back to a
 *      hardcoded string.
 */
(() => {
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const CART_UPDATE_EVENT = 'cart:update';

  const isFeeLine = (item) =>
    item.product_type === 'mws_fee_generated' || item.handle === 'additional-fees';

  /* ------------------------------------------------------------------ *
   * 1. Remove a cart that contains nothing but the generated fee.
   * ------------------------------------------------------------------ */

  // Capped so that if the app ever re-adds the fee we stop rather than loop forever.
  const MAX_ATTEMPTS = 2;
  let attempts = 0;
  let busy = false;

  async function removeOrphanedFee() {
    if (busy || attempts >= MAX_ATTEMPTS) return;
    busy = true;

    try {
      // Read the authoritative cart immediately before acting, so a product added between
      // the triggering event and now cannot be cleared out from under the customer.
      const response = await fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;

      const cart = await response.json();
      if (!cart.items || cart.items.length === 0) return; // already empty
      if (!cart.items.every(isFeeLine)) return; // real merchandise present, leave it alone

      attempts += 1;

      const cleared = await fetch(`${ROOT}cart/clear.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      });
      if (!cleared.ok) return;

      // Let the theme re-render. Horizon reads `detail.data`, and with no `sections` payload
      // cart-items-component falls back to re-rendering the section from the server.
      document.dispatchEvent(
        new CustomEvent(CART_UPDATE_EVENT, {
          bubbles: true,
          detail: {
            resource: {},
            sourceId: 'upcharge-fee-bridge',
            data: { itemCount: 0, merchandiseCount: 0, source: 'upcharge-fee-bridge' },
          },
        })
      );
    } catch (_) {
      // Network hiccup: leave the cart untouched rather than guess at its state.
    } finally {
      busy = false;
    }
  }

  /* ------------------------------------------------------------------ *
   * 2. Mirror the app's fee explanation into the theme's tooltip.
   * ------------------------------------------------------------------ */

  // `-locked` is present when the merchant has filled in Theme settings -> Cart -> Service fee
  // explanation. That wins, and the app is not consulted at all.
  const TARGET_SELECTOR = '[data-fee-explanation]:not([data-fee-explanation-locked])';

  // Two different app settings can supply this text, in preference order.
  const SOURCES = [
    // 1. The fee's own description — the field edited per-fee in the app.
    //    For a hidden fee the app does:
    //      this.feeDescription(fee) && this.createTooltip(this.feeDescription(fee), ...)
    //    and createTooltip hands the string straight to Tippy's JS API
    //    (`window.mwFeesTippy(el, { content })`). It is never written to an attribute, so it
    //    has to be read off the Tippy instance hanging on the element.
    () => {
      const el = document.querySelector('.mw-hidden-fee-tooltip');
      const content = el && el._tippy && el._tippy.props && el._tippy.props.content;
      return typeof content === 'string' ? content : '';
    },
    // 2. The "hidden fee notice" description — a separate setting, and the only one the app
    //    does expose as an attribute (via attachHiddenFeeTooltip).
    () => {
      const el = document.querySelector('.mw-hidden-fee-notice__icon[data-tippy-content]');
      return el ? el.getAttribute('data-tippy-content') || '' : '';
    },
  ];

  // The app's content may contain markup. Parse it inertly — DOMParser neither runs scripts
  // nor loads resources — and keep the plain text so it inherits the theme's tooltip styling.
  function toPlainText(value) {
    if (!value) return '';
    if (value.indexOf('<') === -1) return value.replace(/\s+/g, ' ').trim();
    const doc = new DOMParser().parseFromString(value, 'text/html');
    return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
  }

  function syncFeeExplanation() {
    // Nothing to do if the theme setting is authoritative.
    if (!document.querySelector(TARGET_SELECTOR)) return false;

    let text = '';
    for (const read of SOURCES) {
      try {
        text = toPlainText(read());
      } catch (_) {
        text = '';
      }
      if (text) break;
    }
    if (!text) return false;

    for (const target of document.querySelectorAll(TARGET_SELECTOR)) {
      if (target.textContent.trim() !== text) target.textContent = text;
    }
    return true;
  }

  /* ------------------------------------------------------------------ */

  let scheduled;
  function run() {
    clearTimeout(scheduled);
    // Debounced: UpCharge fires several cart cycles per action.
    scheduled = setTimeout(() => {
      removeOrphanedFee();
      syncFeeExplanation();
    }, 300);
  }

  document.addEventListener(CART_UPDATE_EVENT, run);
  document.addEventListener('cart-update', run);

  // Refresh the moment the tooltip is opened, so the text is right even if the app initialised
  // its widget after the retries below had finished. `beforetoggle` does not bubble, so this
  // listens on the capture phase.
  document.addEventListener(
    'beforetoggle',
    (event) => {
      const target = event.target;
      if (target && target.matches && target.matches(TARGET_SELECTOR)) syncFeeExplanation();
    },
    true
  );

  // The app injects its notice asynchronously after load, so retry briefly rather than
  // installing a permanent observer for a string that only changes between sessions.
  [0, 600, 1800, 4000].forEach((delay) =>
    setTimeout(() => {
      if (delay === 0) removeOrphanedFee();
      syncFeeExplanation();
    }, delay)
  );
})();
